import React, { useState, useMemo } from 'react'
import { ChevronUp, ChevronDown, X, Sparkles, Check, Minus, TrendingUp, Loader2, MessageCircle, ChevronRight } from 'lucide-react'
import { usePlayerStats, useGameOdds, useGamePreview, usePrefetch, usePulse } from '../hooks/useGames'
import TeamLogo from './TeamLogo'
import PlayerCardV2 from './PlayerCardV2'

// Player Card - Bottom Sheet Style
function PlayerDetailDrawer({ player, onClose, onAskAI, gameContext }) {
  if (!player) return null
  
  // Fetch player stats (season avg + recent games) - cached for 24h on server
  const playerId = player.player?.id || player.id
  const { data: playerData, isLoading } = usePlayerStats(playerId)
  
  // Season averages from API
  const seasonAvg = playerData?.seasonAverages
  const rawRecentGames = playerData?.recentGames || []
  
  // Use recent games - already sorted by API
  const recentGames = rawRecentGames.slice(0, 10)
  
  // Format game info - show opponent and date
  const formatGameInfo = (game) => {
    if (!game?.game) return '--'
    
    // Get opponent - if player's team is home, opponent is visitor and vice versa
    const homeTeam = game.game.home_team?.abbreviation || game.game.home_team?.name || ''
    const visitorTeam = game.game.visitor_team?.abbreviation || game.game.visitor_team?.name || ''
    const playerTeamId = game.player?.team_id || player.player?.team_id
    
    // Determine opponent
    let opponent = ''
    let isHome = false
    if (game.game.home_team?.id === playerTeamId) {
      opponent = visitorTeam
      isHome = true
    } else {
      opponent = homeTeam
      isHome = false
    }
    
    // Format: "vs LAL" or "@ LAL" with date
    const prefix = isHome ? 'vs' : '@'
    const dateStr = game.game.date
    if (dateStr) {
      const date = new Date(dateStr)
      const formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      return `${prefix} ${opponent} (${formatted})`
    }
    return `${prefix} ${opponent}`
  }
  
  // Build prompt for AI analysis - INCLUDE THE DATA so agent has it
  const buildAnalysisPrompt = () => {
    const name = player.name || `${player.player?.first_name || ''} ${player.player?.last_name || ''}`.trim()
    
    // Include actual stats in the prompt
    let statsContext = ''
    if (seasonAvg) {
      statsContext += `\n\nSEASON AVERAGES: ${seasonAvg.pts?.toFixed(1) || 0} PPG, ${seasonAvg.reb?.toFixed(1) || 0} RPG, ${seasonAvg.ast?.toFixed(1) || 0} APG, ${(seasonAvg.fg_pct * 100)?.toFixed(0) || 0}% FG`
    }
    if (recentGames && recentGames.length > 0) {
      const last5 = recentGames.slice(0, 5)
      statsContext += `\n\nLAST ${last5.length} GAMES: ${last5.map(g => `${g.pts}pts`).join(', ')}`
      const avgLast5 = last5.reduce((sum, g) => sum + (g.pts || 0), 0) / last5.length
      statsContext += ` (avg: ${avgLast5.toFixed(1)})`
    }
    
    return `Deep analysis on ${name}. ${statsContext}\n\nGive me: 1) Season averages 2) Last 5 games trend 3) Points line analysis 4) Next game prop recommendations.`
  }
  
  // Calculate hit/miss for recent games based on season avg
  const ptsLine = seasonAvg?.pts || 0
  
  // Get player name and team
  const playerName = player.name || `${player.player?.first_name || ''} ${player.player?.last_name || ''}`.trim() || 'Player'
  const teamName = typeof player.team === 'string' ? player.team : player.team?.abbreviation || player.team?.name || ''
  
  return (
    <div 
      className="fixed inset-0 flex items-end justify-center"
      style={{ zIndex: 9999 }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose} 
      />
      
      {/* Bottom Sheet - centered on screen */}
      <div 
        className="relative bg-white rounded-t-3xl shadow-2xl mx-auto"
        style={{ 
          width: '100%',
          maxWidth: '420px',
          maxHeight: '85vh',
          minHeight: '400px',
          animation: 'slideUp 0.25s ease-out forwards',
          paddingBottom: 'env(safe-area-inset-bottom)',
          left: 0,
          right: 0
        }}
      >
        {/* Drag Handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-gray-200 rounded-full" />
        </div>
        
        {/* Header */}
        <div className="px-6 pb-4 flex items-center justify-between border-b border-gray-100">
          <div>
            <h3 className="text-xl font-bold text-gray-900">{playerName}</h3>
            {teamName && (
              <p className="text-sm text-gray-500 mt-0.5">{teamName}</p>
            )}
          </div>
          <button 
            onClick={onClose} 
            className="p-2.5 hover:bg-gray-100 rounded-full transition -mr-1"
          >
            <X size={22} className="text-gray-400" />
          </button>
        </div>
        
        {/* Scrollable Content */}
        <div 
          className="overflow-y-auto overscroll-contain"
          style={{ maxHeight: 'calc(85vh - 160px)' }}
        >
          <div className="px-6 py-5">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={28} className="animate-spin text-gray-300" />
              </div>
            ) : (
              <>
                {/* Season Averages */}
                {seasonAvg && (
                  <div className="mb-6">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Season Averages</p>
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-gray-50 rounded-2xl p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900">{seasonAvg.pts?.toFixed(1) || '--'}</p>
                        <p className="text-[11px] text-gray-500 mt-1">PPG</p>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900">{seasonAvg.reb?.toFixed(1) || '--'}</p>
                        <p className="text-[11px] text-gray-500 mt-1">RPG</p>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900">{seasonAvg.ast?.toFixed(1) || '--'}</p>
                        <p className="text-[11px] text-gray-500 mt-1">APG</p>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-4 text-center">
                        <p className="text-2xl font-bold text-gray-900">{seasonAvg.fg_pct ? (seasonAvg.fg_pct * 100).toFixed(0) : '--'}</p>
                        <p className="text-[11px] text-gray-500 mt-1">FG%</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Recent Games */}
                {recentGames.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Recent Games</p>
                      {ptsLine > 0 && (
                        <span className="text-[11px] text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                          vs {ptsLine.toFixed(1)} PTS
                        </span>
                      )}
                    </div>
                    <div className="space-y-2.5">
                      {recentGames.slice(0, 5).map((game, i) => {
                        const gamePts = game?.pts ?? 0
                        const hitPts = ptsLine > 0 && gamePts >= ptsLine
                        
                        return (
                          <div key={i} className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-2xl">
                            <span className="text-[11px] text-gray-500 w-28 truncate">
                              {formatGameInfo(game)}
                            </span>
                            <div className="flex items-center gap-4 flex-1 justify-center">
                              <span className="text-sm font-semibold text-gray-900">{gamePts} <span className="text-gray-400 font-normal text-xs">PTS</span></span>
                              <span className="text-sm text-gray-600">{game?.reb ?? 0} <span className="text-gray-400 text-xs">REB</span></span>
                              <span className="text-sm text-gray-600">{game?.ast ?? 0} <span className="text-gray-400 text-xs">AST</span></span>
                            </div>
                            {ptsLine > 0 && (
                              <span className={`text-[10px] font-bold w-8 text-center py-1.5 rounded-lg ${hitPts ? 'bg-green-100 text-green-600' : 'bg-red-50 text-red-500'}`}>
                                {hitPts ? 'O' : 'U'}
                              </span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
                
                {/* Betting Insights - placeholder for future */}
                {seasonAvg && recentGames.length > 0 && (
                  <div className="mb-6">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Prop Trends</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-gray-50 rounded-2xl p-4">
                        <p className="text-[11px] text-gray-500 mb-1">Points O/{ptsLine.toFixed(1)}</p>
                        <p className="text-lg font-bold text-gray-900">
                          {recentGames.slice(0, 5).filter(g => (g?.pts ?? 0) >= ptsLine).length}/5
                          <span className="text-sm font-normal text-gray-400 ml-1">hit</span>
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-2xl p-4">
                        <p className="text-[11px] text-gray-500 mb-1">Avg vs Line</p>
                        <p className="text-lg font-bold text-gray-900">
                          {(() => {
                            const avg = recentGames.slice(0, 5).reduce((sum, g) => sum + (g?.pts ?? 0), 0) / Math.min(5, recentGames.length)
                            const diff = avg - ptsLine
                            return `${diff >= 0 ? '+' : ''}${diff.toFixed(1)}`
                          })()}
                          <span className="text-sm font-normal text-gray-400 ml-1">pts</span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* No data state */}
                {!seasonAvg && recentGames.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-sm text-gray-400">No stats available</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        
        {/* Fixed CTA at bottom */}
        <div className="px-6 py-4 border-t border-gray-100 bg-white">
          <button
            onClick={() => {
              onAskAI?.(buildAnalysisPrompt())
              onClose()
            }}
            className="w-full py-3.5 text-gray-700 text-sm font-semibold rounded-2xl flex items-center justify-center gap-2 transition-all hover:bg-gray-100 active:scale-[0.98] border border-gray-200"
          >
            <Sparkles size={16} />
            Get AI Analysis
          </button>
        </div>
      </div>
      
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}

// Extract team name - remove city for NFL to match NBA style
function getTeamDisplayName(teamName, league) {
  if (!teamName) return 'Team'
  if (league !== 'nfl') return teamName
  
  // NFL: "Tampa Bay Buccaneers" → "Buccaneers"
  // Extract last word(s) that are the team name
  const parts = teamName.split(' ')
  if (parts.length <= 1) return teamName
  
  // Common NFL team name patterns (last 1-2 words)
  const lastWord = parts[parts.length - 1]
  const secondLastWord = parts[parts.length - 2]
  
  // Teams with 2-word names (e.g., "Bay Packers", "York Giants")
  const twoWordTeams = ['Bay', 'York', 'Orleans', 'England', 'Island', 'Jose']
  if (twoWordTeams.includes(secondLastWord)) {
    return `${secondLastWord} ${lastWord}`
  }
  
  // Default: just last word (e.g., "Buccaneers", "Falcons")
  return lastWord
}

// Game Preview for Scheduled Games
function GamePreview({ game, league, onAskAI, onSelectPlayer }) {
  // ALL HOOKS MUST BE AT THE TOP - before any conditional returns
  const [selectedTeamIdx, setSelectedTeamIdx] = useState(0)
  const [selectedPropType, setSelectedPropType] = useState('points')
  const [selectedBook, setSelectedBook] = useState('draftkings') // DK or FD toggle
  const [showAllPlayers, setShowAllPlayers] = useState(false)
  const [showAwayML, setShowAwayML] = useState(false) // ML toggle state - moved up
  
  const INITIAL_PLAYERS = 5
  const gameId = game?.raw?.id || game?.id
  const gameLeague = league || game?.league || game?.raw?.league || 'nba'
  const { data: preview, isLoading, isError } = useGamePreview(gameId, gameLeague)
  const { prefetchPlayerStats } = usePrefetch()
  // Only show loading if actually loading AND no data yet (placeholder is fine to show)
  const isPreviewLoading = isLoading && !preview
  
  const homeTeam = preview?.homeTeam?.abbreviation || game?.teams?.[0]?.code || 'Home'
  const awayTeam = preview?.awayTeam?.abbreviation || game?.teams?.[1]?.code || 'Away'
  const homeTeamName = preview?.homeTeam?.name || game?.teams?.[0]?.name || 'Home'
  const awayTeamName = preview?.awayTeam?.name || game?.teams?.[1]?.name || 'Away'
  const odds = preview?.odds
  const playerProps = preview?.playerProps || []
  
  
  
  // Handle player click - pass all props for PlayerCardV2
  const handlePlayerClick = (playerProp) => {
    console.log('[GameDetailDrawer] Player clicked:', playerProp.playerName)
    if (onSelectPlayer) {
      onSelectPlayer({
        id: playerProp.playerId,
        name: playerProp.playerName,
        team: playerProp.team,
        player: { id: playerProp.playerId },
        // Pass all player props for the card
        allProps: playerProps,
        myProps: playerProp.props,
      })
    }
  }
  
  if (isPreviewLoading) {
    return (
      <div className="space-y-3 mt-3">
        {/* Single clean skeleton loader */}
        <div className="rounded-lg border border-gray-100 bg-white overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
            <div className="h-3 bg-gray-200 rounded animate-pulse w-20"></div>
          </div>
          <div className="divide-y divide-gray-50">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2 flex-1">
                  <div className="h-3 bg-gray-200 rounded animate-pulse w-8"></div>
                  <div className="h-3 bg-gray-200 rounded animate-pulse flex-1"></div>
                </div>
                <div className="h-3 bg-gray-200 rounded animate-pulse w-12"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }
  
  // Filter valid props and group by team (allow line-only props, bucket unknown teams)
  const validProps = playerProps.filter(p => p.playerName && !p.playerName.includes('#'))
  const homeProps = validProps.filter(p => p.team === homeTeam)
  const awayProps = validProps.filter(p => p.team === awayTeam)
  const unknownProps = validProps.filter(p => !p.team || p.team === '')
  const teams = [
    { name: homeTeamName, abbr: homeTeam, props: homeProps },
    { name: awayTeamName, abbr: awayTeam, props: awayProps },
    ...(unknownProps.length > 0 ? [{ name: 'Unknown', abbr: 'UNK', props: unknownProps }] : [])
  ]
  const currentTeam = teams[selectedTeamIdx]
  
  // Get matchup data from preview
  const matchup = preview?.matchup
  
  return (
    <div className="space-y-3 mt-3">
      {/* Matchup Intelligence - ESPN style */}
      {matchup && (
        <div className="rounded-xl overflow-hidden border border-gray-100 bg-white">
          <div className="px-3 py-2 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Matchup</p>
          </div>
          <div className="p-3 space-y-2">
            {/* Team comparison */}
            <div className="grid grid-cols-2 gap-3">
              <div className="text-center">
                <p className="text-[11px] font-bold text-gray-800">{awayTeam}</p>
                <p className={`text-[9px] font-semibold px-2 py-0.5 rounded-full inline-block mt-1 ${
                  matchup.awayTier === 'Elite' ? 'bg-emerald-100 text-emerald-700' :
                  matchup.awayTier === 'Strong' ? 'bg-blue-100 text-blue-700' :
                  matchup.awayTier === 'Average' ? 'bg-gray-100 text-gray-600' :
                  'bg-red-100 text-red-600'
                }`}>{matchup.awayTier || 'Average'}</p>
                <div className="mt-1.5 text-[9px] text-gray-500">
                  <span className="font-medium">Off #{matchup.awayOffRank || '--'}</span>
                  <span className="mx-1">•</span>
                  <span className="font-medium">Def #{matchup.awayDefRank || '--'}</span>
                </div>
              </div>
              <div className="text-center">
                <p className="text-[11px] font-bold text-gray-800">{homeTeam}</p>
                <p className={`text-[9px] font-semibold px-2 py-0.5 rounded-full inline-block mt-1 ${
                  matchup.homeTier === 'Elite' ? 'bg-emerald-100 text-emerald-700' :
                  matchup.homeTier === 'Strong' ? 'bg-blue-100 text-blue-700' :
                  matchup.homeTier === 'Average' ? 'bg-gray-100 text-gray-600' :
                  'bg-red-100 text-red-600'
                }`}>{matchup.homeTier || 'Average'}</p>
                <div className="mt-1.5 text-[9px] text-gray-500">
                  <span className="font-medium">Off #{matchup.homeOffRank || '--'}</span>
                  <span className="mx-1">•</span>
                  <span className="font-medium">Def #{matchup.homeDefRank || '--'}</span>
                </div>
              </div>
            </div>
            {/* Edge callout */}
            {matchup.edge && (
              <p className="mt-2 text-[10px] text-amber-700 font-medium text-center">{matchup.edge}</p>
            )}
          </div>
        </div>
      )}
      
      {/* Betting Lines with DK/FD toggle */}
      {odds && (() => {
        // Get odds for selected book, fallback to default
        const bookKey = selectedBook === 'draftkings' ? 'dk' : 'fd'
        const bookOdds = odds.books?.[bookKey] || odds
        const spread = bookOdds.spread || bookOdds.spread_home_value || odds.spread_home_value
        const total = bookOdds.total || bookOdds.total_value || odds.total_value
        const mlAway = bookOdds.moneyline?.away ?? bookOdds.moneyline_away_odds ?? odds.moneyline_away_odds
        const mlHome = bookOdds.moneyline?.home ?? bookOdds.moneyline_home_odds ?? odds.moneyline_home_odds
        
        return (
          <div className="flex items-center gap-1">
            <div className="flex-1 rounded-md border border-gray-200 bg-white px-1.5 py-1 text-center">
              <p className="text-[7px] text-gray-400 uppercase">Spread</p>
              <p className="text-[13px] font-semibold text-gray-700">{spread || '--'}</p>
            </div>
            <div className="flex-1 rounded-md border border-gray-200 bg-white px-1.5 py-1 text-center">
              <p className="text-[7px] text-gray-400 uppercase">O/U</p>
              <p className="text-[13px] font-semibold text-gray-700">{total || '--'}</p>
            </div>
            <div className="flex-1 rounded-md border border-green-100 bg-green-50 px-1.5 py-1 text-center">
              <p className="text-[7px] text-green-600 font-medium">{awayTeam}</p>
              <p className="text-[13px] font-semibold text-green-700">
                {mlAway > 0 ? '+' : ''}{mlAway || '--'}
              </p>
            </div>
            <div className="flex-1 rounded-md border border-orange-100 bg-orange-50 px-1.5 py-1 text-center">
              <p className="text-[7px] text-orange-600 font-medium">{homeTeam}</p>
              <p className="text-[13px] font-semibold text-orange-700">
                {mlHome > 0 ? '+' : ''}{mlHome || '--'}
              </p>
            </div>
            <button 
              onClick={() => setSelectedBook(selectedBook === 'draftkings' ? 'fanduel' : 'draftkings')}
              className="rounded-md bg-gray-800 px-2 py-2.5 text-[10px] font-medium text-white"
            >
              {selectedBook === 'draftkings' ? 'DK' : 'FD'}
            </button>
          </div>
        )
      })()}
      
      {/* Player Props - Both teams combined, no toggle */}
      {validProps.length > 0 && (() => {
        // Prop types differ by league
        const nbaPropTypes = [
          { key: 'points', label: 'PTS' },
          { key: 'rebounds', label: 'REB' },
          { key: 'assists', label: 'AST' },
          { key: 'threes', label: '3PM' },
          { key: 'points_rebounds_assists', label: 'PRA' },
        ]
        const nflPropTypes = [
          { key: 'passing_yards', label: 'Pass Yds' },
          { key: 'passing_tds', label: 'Pass TD' },
          { key: 'passing_completions', label: 'Comp' },
          { key: 'interceptions', label: 'INT' },
          { key: 'rushing_yards', label: 'Rush Yds' },
          { key: 'rushing_attempts', label: 'Rush Att' },
          { key: 'receiving_yards', label: 'Rec Yds' },
          { key: 'receptions', label: 'Rec' },
          { key: 'anytime_td', label: 'Any TD' },
          { key: 'first_td', label: '1st TD' },
        ]
        const isNFL = gameLeague?.toLowerCase() === 'nfl'
        const propTypes = isNFL ? nflPropTypes : nbaPropTypes
        const effectivePropType = propTypes.some(pt => pt.key === selectedPropType) 
          ? selectedPropType 
          : (propTypes[0]?.key || 'points')
        
        const getPlayerPropForBook = (playerProps, propType, book) => {
          // Prioritize props with odds over milestone props
          const allOfType = playerProps?.filter(p => p.type === propType) || []
          const bookProps = allOfType.filter(p => 
            p.sportsbook === book || 
            (book === 'draftkings' && p.sportsbook?.includes('draft')) ||
            (book === 'fanduel' && p.sportsbook?.includes('fanduel'))
          )
          // First try to find a prop with odds for this book
          const propWithOdds = bookProps.find(p => p.overOdds != null)
          if (propWithOdds) return propWithOdds
          // Fall back to any prop for this book
          if (bookProps.length > 0) return bookProps[0]
          // Last resort: any prop with odds
          const anyPropWithOdds = allOfType.find(p => p.overOdds != null)
          if (anyPropWithOdds) return anyPropWithOdds
          // Absolute fallback
          return allOfType[0]
        }
        
        // Combine both teams, add team abbr to each player (allow line-only props)
        const allPlayersWithProp = teams.flatMap((team, teamIdx) => 
          team.props
            .filter(p => p.props?.some(prop => prop.type === effectivePropType))
            .map(p => ({ ...p, teamAbbr: team.abbr, teamIdx }))
        ).sort((a, b) => {
          const aProp = getPlayerPropForBook(a.props, effectivePropType, selectedBook)
          const bProp = getPlayerPropForBook(b.props, effectivePropType, selectedBook)
          return parseFloat(bProp?.line || '0') - parseFloat(aProp?.line || '0')
        })
        
        const displayPlayers = showAllPlayers ? allPlayersWithProp : allPlayersWithProp.slice(0, INITIAL_PLAYERS)
        
        return (
          <div className="rounded-lg border border-gray-100 bg-white overflow-hidden">
            {/* Stat type chips - horizontal scroll carousel */}
            <div className="px-3 py-2 border-b border-gray-100">
              <div className="flex gap-1.5 overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {propTypes.map(pt => (
                  <button
                    key={pt.key}
                    onClick={() => setSelectedPropType(pt.key)}
                    className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all whitespace-nowrap"
                    style={{
                      background: effectivePropType === pt.key ? '#1F2937' : '#F3F4F6',
                      color: effectivePropType === pt.key ? '#FFFFFF' : '#6B7280',
                    }}
                  >
                    {pt.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Combined player list from both teams */}
            <div className="divide-y divide-gray-50">
              {displayPlayers.length > 0 ? (
                <>
                  {displayPlayers.map((prop) => {
                    const propData = getPlayerPropForBook(prop.props, effectivePropType, selectedBook)
                    if (!propData) return null
                    
                    return (
                      <button
                        key={prop.playerId}
                        type="button"
                        onClick={() => handlePlayerClick(prop)}
                        onMouseEnter={() => prefetchPlayerStats(prop.playerId)}
                        onTouchStart={() => prefetchPlayerStats(prop.playerId)}
                        className="w-full flex items-center justify-between py-2 px-3 hover:bg-gray-50 text-left"
                        style={{ height: '44px' }}
                      >
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span className="text-[10px] text-gray-400 uppercase shrink-0">{prop.teamAbbr}</span>
                          <span className="text-[13px] text-gray-700 truncate">{prop.playerName}</span>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-[13px] font-semibold text-gray-800">{propData.line}</span>
                          {propData.overOdds && (
                            <span className="text-[10px] text-gray-400">o{propData.overOdds > 0 ? '+' : ''}{propData.overOdds}</span>
                          )}
                          <ChevronRight size={12} className="text-gray-300" />
                        </div>
                      </button>
                    )
                  })}
                  {allPlayersWithProp.length > INITIAL_PLAYERS && (
                    <button
                      onClick={() => setShowAllPlayers(!showAllPlayers)}
                      className="w-full py-1.5 text-[11px] font-medium text-gray-500 hover:bg-gray-50 transition"
                    >
                      {showAllPlayers ? 'Less' : `+${allPlayersWithProp.length - INITIAL_PLAYERS} more`}
                    </button>
                  )}
                </>
              ) : (
                <p className="text-[11px] text-gray-400 text-center py-3">No {propTypes.find(p => p.key === effectivePropType)?.label} props</p>
              )}
            </div>
          </div>
        )
      })()}
      
      {validProps.length === 0 && preview && !isPreviewLoading && (
        <p className="text-xs text-gray-400 text-center py-3">
          {isLoading ? 'Loading props...' : (preview?.dataStatus?.hasProps === false ? 'Props not posted yet' : 'No props available')}
        </p>
      )}
      
      {/* Injuries Section - Always visible like NBA */}
      {preview && (
        <div className="rounded-xl border border-gray-100 overflow-hidden bg-white">
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
              Injury Report
            </p>
          </div>
          <div className="divide-y divide-gray-50">
            {isPreviewLoading ? (
              <p className="text-[11px] text-gray-400 text-center py-3">Loading injury report...</p>
            ) : preview.injuries && preview.injuries.length > 0 ? (
              preview.injuries
                .slice(0, 6)
                .map((inj, idx) => {
                  const statusLower = (inj.status || '').toLowerCase()
                  const isOut = statusLower === 'out'
                  const isIR = statusLower.includes('injured reserve') || statusLower === 'ir'
                  const isDTD = statusLower.includes('day-to-day') || statusLower.includes('dtd') || statusLower.includes('questionable')
                  
                  // Format status badge - show full text for clarity
                  let badge = inj.status
                  let badgeColor = 'bg-gray-50 text-gray-500'
                  if (isIR) {
                    badge = 'IR'
                    badgeColor = 'bg-gray-200 text-gray-600'
                  } else if (isOut) {
                    badge = 'OUT'
                    badgeColor = 'bg-gray-100 text-gray-600'
                  } else if (isDTD) {
                    badge = 'DTD'
                    badgeColor = 'bg-amber-50 text-amber-600'
                  }
                  
                  return (
                    <div key={idx} className="px-3 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-medium text-gray-400 w-8">{inj.team}</span>
                        <span className="text-[12px] text-gray-700">{inj.playerName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-gray-400">{inj.description || ''}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium uppercase ${badgeColor}`}>
                          {badge}
                        </span>
                      </div>
                    </div>
                  )
                })
            ) : (
              <p className="text-[11px] text-gray-400 text-center py-3">No injuries reported</p>
            )}
          </div>
        </div>
      )}
      
      {/* Quick Actions */}
      <div className="flex gap-2 pt-1">
        <button
          onClick={() => {
            // Build context-rich prompt with actual data
            const isNFL = gameLeague?.toLowerCase() === 'nfl'
            const topPlayers = validProps.slice(0, 3).map(p => {
              const ptsProp = p.props?.find(pr => pr.type === 'points')
              return `${p.playerName} (${ptsProp?.line || '?'} pts)`
            }).join(', ')
            const injuredPlayers = preview?.injuries?.slice(0, 3).map(i => `${i.playerName} (${i.status})`).join(', ') || 'none'
            const spread = odds?.spread?.home || '?'
            const total = odds?.total || '?'
            
            const prompt = `${isNFL ? '[NFL] ' : ''}Quick breakdown of ${awayTeam} @ ${homeTeam}:
- Spread: ${homeTeam} ${spread > 0 ? '+' : ''}${spread}
- Total: ${total}
- Key props: ${topPlayers}
- Injuries: ${injuredPlayers}

What's the play here? Any edges?`
            onAskAI?.(prompt, { league: gameLeague })
          }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium text-gray-600 transition hover:bg-gray-100 active:scale-[0.98] border"
          style={{ borderColor: '#E5E7EB' }}
        >
          <Sparkles size={12} />
          Analyze
        </button>
        <button
          onClick={() => {
            // Build props-focused prompt with actual lines
            const isNFL = gameLeague?.toLowerCase() === 'nfl'
            const topProps = validProps.slice(0, 5).map(p => {
              const ptsProp = p.props?.find(pr => pr.type === 'points')
              return `${p.playerName}: ${ptsProp?.line || '?'} pts (o${ptsProp?.overOdds || '-110'})`
            }).join('\n')
            
            const prompt = `${isNFL ? '[NFL] ' : ''}Player props for ${awayTeam} @ ${homeTeam}:
${topProps}

Which props look good tonight? Any overs or unders you like?`
            onAskAI?.(prompt, { league: gameLeague })
          }}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium text-gray-600 transition hover:bg-gray-100 active:scale-[0.98] border"
          style={{ borderColor: '#E5E7EB' }}
        >
          <TrendingUp size={12} />
          Props
        </button>
      </div>
    </div>
  )
}

// Pace Bar - clean O/U progress matching UI standard
// Score on LEFT until target hit, then full green bar
function PaceBar({ game }) {
  const gameId = game?.id || game?.raw?.id
  const storageKey = `closing_line_${gameId}`
  
  // Check if game is final
  const status = game?.status || game?.raw?.status || ''
  const isFinal = status.toLowerCase().includes('final') || status === 'F'
  
  // Get closing line
  let closingLineValue = NaN
  const serverClosing = game.closingLine?.total || game.raw?.closingLine?.total
  if (serverClosing) {
    closingLineValue = parseFloat(serverClosing)
    try { localStorage.setItem(storageKey, String(closingLineValue)) } catch {}
  }
  if (Number.isNaN(closingLineValue) || closingLineValue === 0) {
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) closingLineValue = parseFloat(stored)
    } catch {}
  }
  if (Number.isNaN(closingLineValue) || closingLineValue === 0) {
    const currentLine = game.odds?.total || game.raw?.odds?.total
    if (currentLine) {
      closingLineValue = parseFloat(currentLine)
      try { localStorage.setItem(storageKey, String(closingLineValue)) } catch {}
    }
  }
  
  if (!closingLineValue || Number.isNaN(closingLineValue)) return null
  
  const closingLine = closingLineValue
  const totalScore = (game.teams?.[0]?.score || 0) + (game.teams?.[1]?.score || 0)
  const hitTarget = totalScore >= closingLine
  
  // Progress: fill bar up to target marker position, then full when hit
  // Target marker is at 87% of bar width (leaving room for label)
  const targetPct = 87
  const progressPct = hitTarget ? 100 : Math.min(targetPct, (totalScore / closingLine) * targetPct)
  
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-3 mb-2">
      {/* Label row */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wide">
          {isFinal ? 'Final O/U' : 'O/U Pace'}
        </span>
        <span className="text-[10px] text-gray-400">Target: {closingLine}</span>
      </div>
      
      {/* Progress bar with score on left, target on right */}
      <div className="flex items-center gap-2">
        {/* Score - always on left */}
        <span 
          className="text-[12px] font-bold tabular-nums min-w-[32px]"
          style={{ color: hitTarget ? '#22C55E' : '#FF4D6D' }}
        >
          {totalScore}
        </span>
        
        {/* Bar container */}
        <div className="flex-1 relative h-2">
          <div className="absolute inset-0 bg-gray-100 rounded-full" />
          <div 
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
            style={{ 
              width: `${progressPct}%`, 
              background: hitTarget ? '#22C55E' : '#FF4D6D',
            }}
          />
          {/* Target marker - only show if not hit */}
          {!hitTarget && (
            <div 
              className="absolute top-1/2 -translate-y-1/2 w-0.5 h-4 bg-gray-400 rounded-full"
              style={{ left: `${targetPct}%` }}
            />
          )}
        </div>
        
        {/* Result indicator */}
        {isFinal && (
          <span 
            className="text-[10px] font-semibold"
            style={{ color: hitTarget ? '#22C55E' : '#FF4D6D' }}
          >
            {hitTarget ? '✓ Over' : '✗ Under'}
          </span>
        )}
      </div>
    </div>
  )
}

// Game Injuries - filtered by teams in this game
function GameInjuries({ game, league }) {
  // Get team abbreviations from normalized game object
  // After normalization, teams array has: { code, name, ... }
  const homeTeamObj = game?.teams?.[0]
  const awayTeamObj = game?.teams?.[1]
  
  // The 'code' field contains the team abbreviation (e.g., "IND", "SF")
  // This matches the injury data format which has team as abbreviation string
  const homeAbbr = (homeTeamObj?.code || '').toUpperCase()
  const awayAbbr = (awayTeamObj?.code || '').toUpperCase()
  
  // Fetch pulse WITHOUT team filter - we'll filter client-side where we have full control
  const { data: pulseData } = usePulse(league || 'nba', '')
  const [expanded, setExpanded] = useState(false)
  
  const injuries = useMemo(() => {
    const items = pulseData?.items || []
    // Create set of team abbreviations for this game
    const teamSet = new Set([homeAbbr, awayAbbr])
    
    return items
      .filter(i => (i.type === 'injury' || i.type === 'status_change'))
      .filter(i => {
        const itemTeam = (i.team || '').toUpperCase()
        // Match if item team abbreviation is in our set
        return teamSet.has(itemTeam)
      })
      .slice(0, expanded ? 8 : 4)
  }, [pulseData, homeAbbr, awayAbbr, expanded])
  
  const totalCount = useMemo(() => {
    const items = pulseData?.items || []
    const teamSet = new Set([homeAbbr, awayAbbr])
    
    return items
      .filter(i => (i.type === 'injury' || i.type === 'status_change'))
      .filter(i => {
        const itemTeam = (i.team || '').toUpperCase()
        return teamSet.has(itemTeam)
      }).length
  }, [pulseData, homeAbbr, awayAbbr])
  
  if (injuries.length === 0) return null
  
  const getStatusBadge = (item) => {
    // Use status field directly from API (injury.status or newStatus for status_change)
    let statusStr = ''
    if (item.status) {
      // Direct status field from injury data
      statusStr = item.status.toLowerCase()
    } else if (item.newStatus) {
      // Status change format
      statusStr = item.newStatus.toLowerCase()
    } else if (item.title) {
      // Fallback: extract status from "PlayerName - Status" format
      const parts = item.title.split(' - ')
      statusStr = parts[parts.length - 1].toLowerCase()
    }
    
    if (statusStr.includes('out') || statusStr.includes('ruled out')) return { label: 'OUT', color: 'bg-red-100 text-red-600' }
    if (statusStr.includes('questionable') || statusStr.includes('gtd')) return { label: 'GTD', color: 'bg-yellow-100 text-yellow-700' }
    if (statusStr.includes('doubtful')) return { label: 'DBT', color: 'bg-orange-100 text-orange-700' }
    if (statusStr.includes('probable')) return { label: 'PROB', color: 'bg-green-100 text-green-600' }
    if (statusStr.includes('cleared') || statusStr.includes('available')) return { label: 'OK', color: 'bg-green-100 text-green-600' }
    if (statusStr.includes('day-to-day') || statusStr.includes('dtd')) return { label: 'DTD', color: 'bg-amber-100 text-amber-700' }
    return { label: 'INJ', color: 'bg-gray-100 text-gray-600' }
  }
  
  return (
    <div className="mt-3 mx-1 bg-white rounded-lg border border-gray-100">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-3 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Injuries</span>
          <span className="text-[9px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">{totalCount}</span>
        </div>
        <ChevronDown size={12} className={`text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      
      {expanded && (
        <div className="divide-y divide-gray-50 border-t border-gray-50">
          {injuries.map((item, idx) => {
            const badge = getStatusBadge(item)
            const playerName = item.player || item.title?.split(' - ')[0] || 'Unknown'
            const team = item.team || ''
            
            // Extract status from description - show what changed
            let statusText = ''
            if (item.type === 'status_change') {
              // Status change format: "Player: OldStatus → NewStatus"
              if (item.previousStatus && item.newStatus) {
                statusText = `${item.previousStatus} → ${item.newStatus}`
              } else {
                statusText = item.description || ''
              }
            } else {
              // Regular injury - show description
              statusText = item.description || ''
            }
            
            // Create unique key from player name, team, and index to avoid duplicates
            const uniqueKey = `injury-${playerName}-${team}-${idx}`
            
            return (
              <div key={uniqueKey} className="px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[11px] font-semibold text-gray-900">{playerName}</span>
                      {team && <span className="text-[9px] text-gray-500 font-medium">{team}</span>}
                    </div>
                    {statusText && (
                      <p className="text-[9px] text-gray-500 mt-0.5">{statusText}</p>
                    )}
                  </div>
                  <span className={`text-[8px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0 ${badge.color}`}>{badge.label}</span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function GameDetailDrawer({ game, league, onAddContext, isExpanded, onToggle, onBack, onAskAI }) {
  const [selectedTeamIdx, setSelectedTeamIdx] = useState(0)
  const [showBench, setShowBench] = useState(false)
  const [selectedPlayer, setSelectedPlayer] = useState(null)
  const [sortBy, setSortBy] = useState('pts') // Default sort by points
  const [sortDir, setSortDir] = useState('desc')
  
  // Fetch preview data for player props (works for live games too)
  const gameId = game?.raw?.id || game?.id
  const gameLeague = league || game?.league || game?.raw?.league || 'nba'
  const { data: preview } = useGamePreview(gameId, gameLeague)
  
  // Merge playerProps from preview if not already on game
  const playerProps = game?.playerProps?.length > 0 ? game.playerProps : (preview?.playerProps || [])
  
  
  
  // Build game context string for AI
  const gameContext = game?.teams ? `${game.teams[1]?.name || 'Away'} vs ${game.teams[0]?.name || 'Home'}` : ''
  const STARTER_MIN_THRESHOLD = 15 // Players with 15+ min are likely starters

  if (!game) return null

  // Get all players from each team
  // NFL games: check game.raw.players (original API data) or game.teams[].players (normalized)
  // NBA games: game.teams[].players
  const getTeams = () => {
    const baseTeams = game.teams || []
    
    // For NFL, check if players need to be merged from raw API data
    // game.raw contains the original API response with players.home/away
    const rawPlayers = game.raw?.players || game.players
    if (gameLeague?.toLowerCase() === 'nfl' && rawPlayers) {
      return baseTeams.map((team, idx) => ({
        ...team,
        players: idx === 0 ? (rawPlayers.home || team.players || []) : (rawPlayers.away || team.players || [])
      }))
    }
    
    return baseTeams
  }

  const teams = getTeams()
  const currentTeam = teams[selectedTeamIdx]
  
  // Filter players who actually played (have minutes or points)
  const activePlayers = currentTeam?.players?.filter(p => 
    (p.min && p.min > 0) || (p.pts && p.pts > 0) || (p.reb && p.reb > 0)
  ) || []
  
  // Sort players
  const sortPlayers = (players) => {
    return [...players].sort((a, b) => {
      const aVal = a[sortBy] || 0
      const bVal = b[sortBy] || 0
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal
    })
  }
  
  // Split into starters (first 5 players) and bench (rest)
  // Starters are the first 5 who played, sorted by minutes to identify them
  const getMinutes = (p) => {
    if (typeof p.min === 'string') return parseInt(p.min.split(':')[0]) || 0
    return p.min || 0
  }
  
  // Sort by minutes to find the 5 starters, then apply user's sort
  const playersByMinutes = [...activePlayers].sort((a, b) => getMinutes(b) - getMinutes(a))
  const starterIds = new Set(playersByMinutes.slice(0, 5).map(p => p.name))
  
  const starters = sortPlayers(activePlayers.filter(p => starterIds.has(p.name)))
  const bench = sortPlayers(activePlayers.filter(p => !starterIds.has(p.name)))
  
  // Handle column header click for sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir(sortDir === 'desc' ? 'asc' : 'desc')
    } else {
      setSortBy(column)
      setSortDir('desc')
    }
  }
  
  // Get winner for styling
  const homeScore = game.teams?.[0]?.score || 0
  const awayScore = game.teams?.[1]?.score || 0
  const homeWon = homeScore > awayScore
  const awayWon = awayScore > homeScore
  
  // Detect live/final status - handle NFL format like "0:07 - 2nd"
  const statusLower = (game.status || '').toLowerCase()
  const timeLower = (game.time || '').toLowerCase()
  const nflStatusMatch = (game.status || '').match(/^(\d{1,2}:\d{2})\s*-\s*(1st|2nd|3rd|4th|ot)/i)
  // NFL: 0:00 in 4th quarter = FINAL, not live
  const isNFLLive = nflStatusMatch && !(nflStatusMatch[1] === '0:00' && nflStatusMatch[2].toLowerCase() === '4th')
  // Check both status AND time for "final" - API sometimes has status="Q5 5:00" but time="Final"
  const isFinal = statusLower.includes('final') || timeLower.includes('final') || (nflStatusMatch && nflStatusMatch[1] === '0:00' && nflStatusMatch[2].toLowerCase() === '4th')
  const isLive = (game.status === 'LIVE' || statusLower.includes('q') || isNFLLive) && !isFinal
  
  return (
    <div className="space-y-2 pb-4">
      {/* Player Card V2 - ESPN-style betting-native */}
      {selectedPlayer && (
        <PlayerCardV2 
          player={selectedPlayer} 
          onClose={() => setSelectedPlayer(null)} 
          onAskAI={onAskAI}
          gameContext={{ title: gameContext, status: game?.status, date: game?.date || game?.raw?.date, league: gameLeague }}
          injury={selectedPlayer.injury}
          props={selectedPlayer.allProps || playerProps || []}
        />
      )}
      
      {/* PRO MASTHEAD - Centered Score Layout */}
      <div className="rounded-xl border overflow-hidden shadow-sm" style={{ borderColor: '#E5E7EB', backgroundColor: '#fff' }}>
        {/* Back button row */}
        <div className="px-3 py-1.5 bg-gray-50 border-b flex items-center justify-between" style={{ borderColor: '#E5E7EB' }}>
          <button 
            onClick={(e) => {
              e.stopPropagation()
              onBack?.()
            }}
            className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-[#FF2D55] transition"
          >
            <ChevronDown size={14} className="rotate-90" />
            Games
          </button>
          <button onClick={onToggle} className="p-1 hover:bg-gray-200 rounded transition">
            <ChevronUp size={14} className={`text-gray-400 transition-transform ${isExpanded ? '' : 'rotate-180'}`} />
          </button>
        </div>
        
        {/* Masthead - Centered Score */}
        {(!isLive && !isFinal && (game.status === 'SCHEDULED' || game.status === 'UPCOMING' || statusLower.includes('scheduled'))) ? (
          /* Upcoming game layout */
          <div className="px-4 py-4">
            <div className="flex items-center justify-between">
              {/* Away Team */}
              <div className="flex flex-col items-center flex-1 min-w-0">
                <TeamLogo name={game.teams?.[1]?.name} code={game.teams?.[1]?.code} league={gameLeague} size={40} />
                <span className="font-semibold text-xs text-gray-800 mt-1 text-center leading-tight px-1">{getTeamDisplayName(game.teams?.[1]?.name, gameLeague)}</span>
                {game.teams?.[1]?.record && (
                  <span className="text-[10px] text-gray-400">{game.teams?.[1]?.record}</span>
                )}
              </div>
              {/* VS / Time */}
              <div className="flex flex-col items-center px-4 min-w-[80px]">
                <span className="text-xs text-gray-400 font-medium">@</span>
                <span className="text-sm font-semibold text-gray-600 mt-1 text-center whitespace-nowrap">{game.time || 'TBD'}</span>
              </div>
              {/* Home Team */}
              <div className="flex flex-col items-center flex-1 min-w-0">
                <TeamLogo name={game.teams?.[0]?.name} code={game.teams?.[0]?.code} league={gameLeague} size={40} />
                <span className="font-semibold text-xs text-gray-800 mt-1 text-center leading-tight px-1">{getTeamDisplayName(game.teams?.[0]?.name, gameLeague)}</span>
                {game.teams?.[0]?.record && (
                  <span className="text-[10px] text-gray-400">{game.teams?.[0]?.record}</span>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* Live/Final game layout - CLEAN PRO MASTHEAD */
          <div className="px-4 py-3">
            {/* Main Score Row */}
            <div className="flex items-center justify-between">
              {/* Away Team - Left */}
              <div className="flex flex-col items-center flex-1 min-w-0">
                <TeamLogo name={game.teams?.[1]?.name} code={game.teams?.[1]?.code} league={gameLeague} size={40} />
                <span className={`font-semibold text-xs mt-1 text-center leading-tight px-1 ${awayWon ? 'text-gray-900' : 'text-gray-500'}`}>
                  {getTeamDisplayName(game.teams?.[1]?.name, gameLeague)}
                </span>
                {game.teams?.[1]?.record && (
                  <span className="text-[10px] text-gray-400">{game.teams?.[1]?.record}</span>
                )}
              </div>
              
              {/* Center Score Block */}
              <div className="flex flex-col items-center px-2">
                {/* Big Score - slightly smaller */}
                <div className="flex items-center gap-2">
                  <span 
                    className={`text-3xl font-bold ${awayWon ? 'text-gray-900' : 'text-gray-400'}`}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {game.teams?.[1]?.score ?? 0}
                  </span>
                  <span className="text-xl text-gray-300 font-light">—</span>
                  <span 
                    className={`text-3xl font-bold ${homeWon ? 'text-gray-900' : 'text-gray-400'}`}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {game.teams?.[0]?.score ?? 0}
                  </span>
                </div>
                
                {/* Single Clean Status Line */}
                <div className="mt-1.5">
                  {isLive && (
                    <span 
                      className="text-[11px] text-red-600 font-semibold"
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {/* Handle halftime specially, use game.status for NFL which has "15:00 - 4th" format */}
                      {game.time?.toLowerCase().includes('half') || game.status?.toLowerCase().includes('half')
                        ? 'LIVE • Halftime'
                        : game.status?.match(/\d{1,2}:\d{2}\s*-\s*(1st|2nd|3rd|4th|OT)/i)
                          ? `LIVE • ${game.status}`
                          : game.time?.toLowerCase().includes('q') 
                            ? `LIVE • ${game.time}` 
                            : `LIVE • ${game.period >= 5 ? (game.period === 5 ? 'OT' : `${game.period - 4}OT`) : `Q${game.period || 1}`} • ${game.time || '0:00'}`
                      }
                    </span>
                  )}
                  {isFinal && (
                    <span className="text-[11px] text-gray-500 font-semibold">
                      {game.teams?.[0]?.quarters?.length > 4 ? `FINAL/OT` : 'FINAL'}
                    </span>
                  )}
                  {!isLive && !isFinal && (
                    <span className="text-[11px] text-gray-400 font-medium">
                      {game.time || 'Scheduled'}
                    </span>
                  )}
                </div>
                
                {/* NFL Possession/Down - ESPN Data */}
                {game.situation && !isFinal && (
                  <div className="mt-1 flex items-center justify-center gap-1">
                    {game.situation.down === -1 ? (
                      <span className="text-[10px] font-medium text-gray-500">Break</span>
                    ) : (
                      <>
                        {game.situation.possession && (
                          <span className="text-[10px] font-semibold text-pink-600">{game.situation.possession} ball</span>
                        )}
                        {game.situation.possession && game.situation.downDistanceText && <span className="text-gray-300">•</span>}
                        {game.situation.downDistanceText && (
                          <span className="text-[10px] font-medium text-gray-600">{game.situation.downDistanceText}</span>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              
              {/* Home Team - Right */}
              <div className="flex flex-col items-center flex-1 min-w-0">
                <TeamLogo name={game.teams?.[0]?.name} code={game.teams?.[0]?.code} league={gameLeague} size={40} />
                <span className={`font-semibold text-xs mt-1 text-center leading-tight px-1 ${homeWon ? 'text-gray-900' : 'text-gray-500'}`}>
                  {getTeamDisplayName(game.teams?.[0]?.name, gameLeague)}
                </span>
                {game.teams?.[0]?.record && (
                  <span className="text-[10px] text-gray-400">{game.teams?.[0]?.record}</span>
                )}
              </div>
            </div>
            
            {/* Quarter Strip - Pro Scoreboard Style */}
            {game.teams?.[0]?.quarters?.length > 0 && (
              <div className="mt-3 pt-2 border-t border-gray-100">
                <table className="w-full text-[11px]" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <thead>
                    <tr className="text-gray-400">
                      <th className="text-left font-medium py-0.5 w-20"></th>
                      {game.teams[0].quarters.map((_, i) => (
                        <th key={i} className="text-center font-medium py-0.5 w-8">
                          {i < 4 ? `Q${i + 1}` : (i === 4 ? 'OT' : `${i - 3}OT`)}
                        </th>
                      ))}
                      <th className="text-center font-semibold py-0.5 w-10 text-gray-500">T</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr>
                      <td className={`text-left py-0.5 font-medium ${awayWon ? 'text-gray-800' : ''}`}>
                        {getTeamDisplayName(game.teams?.[1]?.name, gameLeague)}
                      </td>
                      {game.teams?.[1]?.quarters?.map((q, i) => (
                        <td key={i} className="text-center py-0.5">{q}</td>
                      ))}
                      <td className={`text-center py-0.5 font-semibold ${awayWon ? 'text-gray-900' : ''}`}>
                        {game.teams?.[1]?.score}
                      </td>
                    </tr>
                    <tr>
                      <td className={`text-left py-0.5 font-medium ${homeWon ? 'text-gray-800' : ''}`}>
                        {getTeamDisplayName(game.teams?.[0]?.name, gameLeague)}
                      </td>
                      {game.teams?.[0]?.quarters?.map((q, i) => (
                        <td key={i} className="text-center py-0.5">{q}</td>
                      ))}
                      <td className={`text-center py-0.5 font-semibold ${homeWon ? 'text-gray-900' : ''}`}>
                        {game.teams?.[0]?.score}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Game Preview for Scheduled Games */}
      {(!isLive && !isFinal && (game.status === 'SCHEDULED' || game.status === 'UPCOMING' || statusLower.includes('scheduled'))) && (
        <GamePreview game={game} league={gameLeague} onAskAI={onAskAI} onSelectPlayer={setSelectedPlayer} />
      )}

      {/* Over/Under Pace Bar - right after score section for live/final games */}
      {(isLive || isFinal) && (
        <PaceBar game={game} />
      )}
      
      {/* Expanded Player Stats - Table Format (not for upcoming games) */}
      {isExpanded && currentTeam && (isLive || isFinal) && (
        <div className="animate-fade-in-up">
          {/* Stats Card with integrated team toggle */}
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: '#E5E7EB', backgroundColor: '#fff' }}>
            {/* Segmented Team Toggle - attached to table */}
            <div className="flex border-b border-gray-200">
              {teams.map((team, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedTeamIdx(idx)}
                  className={`flex-1 py-2.5 text-xs font-semibold transition-all ${
                    selectedTeamIdx === idx 
                      ? 'bg-white text-gray-900 border-b-2 border-pink-500' 
                      : 'bg-gray-50 text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {team.name}
                </button>
              ))}
            </div>

            {/* Stats Table - Sport Specific */}
            {(league?.toLowerCase() === 'nfl' || gameLeague?.toLowerCase() === 'nfl') ? (
              <div className="overflow-x-auto overscroll-x-contain no-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }}>
                {/* Passing Stats - ESPN style: C/ATT, YDS, TD, INT */}
                {currentTeam.players?.filter(p => p.pos?.includes('Quarterback') || p.pos === 'QB' || p.passYds > 0).length > 0 && (
                  <>
                    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                      <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Passing</span>
                    </div>
                    <table className="w-full text-xs" style={{ fontVariantNumeric: 'tabular-nums', minWidth: '320px' }}>
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-left py-2 px-2 font-semibold text-gray-500 sticky left-0 bg-gray-50 z-10" style={{ minWidth: '100px' }}>Player</th>
                          <th className="text-right py-2 px-2 font-semibold text-gray-400">CMP/ATT</th>
                          <th 
                            onClick={() => handleSort('passYds')}
                            className={`text-right py-2 px-2 font-semibold cursor-pointer transition select-none ${sortBy === 'passYds' ? 'text-pink-600' : 'text-gray-400 hover:text-gray-600'}`}
                          >YDS</th>
                          <th 
                            onClick={() => handleSort('passTd')}
                            className={`text-right py-2 px-2 font-semibold cursor-pointer transition select-none ${sortBy === 'passTd' ? 'text-pink-600' : 'text-gray-400 hover:text-gray-600'}`}
                          >TD</th>
                          <th className="text-right py-2 px-2 font-semibold text-gray-400">INT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentTeam.players?.filter(p => p.pos?.includes('Quarterback') || p.pos === 'QB' || p.passYds > 0)
                          .sort((a, b) => {
                            // Sort by TDs first, then yards
                            const aTd = (a.passTd || 0)
                            const bTd = (b.passTd || 0)
                            if (bTd !== aTd) return bTd - aTd
                            return (b.passYds || 0) - (a.passYds || 0)
                          })
                          .map((player, idx) => (
                          <tr 
                            key={idx} 
                            className={`border-b border-gray-100 hover:bg-pink-50/40 transition cursor-pointer ${idx % 2 === 1 ? 'bg-gray-50/50' : ''}`}
                            onClick={() => setSelectedPlayer({ ...player, allProps: playerProps || [] })}
                          >
                            <td className={`py-2 px-2 sticky left-0 z-10 ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                              <span className="text-[11px] text-gray-700 truncate block font-medium">{player.name}</span>
                            </td>
                            <td className="text-right py-2 px-2 text-[11px] text-gray-700">{player.cmp || 0}/{player.att || 0}</td>
                            <td className="text-right py-2 px-2 text-[11px] font-bold text-pink-600">{player.passYds || 0}</td>
                            <td className="text-right py-2 px-2 text-[11px] text-gray-700">{player.passTd || 0}</td>
                            <td className="text-right py-2 px-2 text-[11px] text-gray-700">{player.int || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

              {/* Rushing Stats - ESPN style: CAR, YDS, AVG, TD */}
              {currentTeam.players?.filter(p => p.pos?.includes('Running Back') || p.pos === 'RB' || p.rushYds > 0 || p.rushAtt > 0).length > 0 && (
                <>
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 mt-2">
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Rushing</span>
                  </div>
                  <table className="w-full text-xs" style={{ fontVariantNumeric: 'tabular-nums', minWidth: '320px' }}>
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left py-2 px-2 font-semibold text-gray-500 sticky left-0 bg-gray-50 z-10" style={{ minWidth: '100px' }}>Player</th>
                        <th className="text-right py-2 px-2 font-semibold text-gray-400">CAR</th>
                        <th 
                          onClick={() => handleSort('rushYds')}
                          className={`text-right py-2 px-2 font-semibold cursor-pointer transition select-none ${sortBy === 'rushYds' ? 'text-pink-600' : 'text-gray-400 hover:text-gray-600'}`}
                        >YDS</th>
                        <th className="text-right py-2 px-2 font-semibold text-gray-400">AVG</th>
                        <th 
                          onClick={() => handleSort('rushTd')}
                          className={`text-right py-2 px-2 font-semibold cursor-pointer transition select-none ${sortBy === 'rushTd' ? 'text-pink-600' : 'text-gray-400 hover:text-gray-600'}`}
                        >TD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentTeam.players?.filter(p => p.pos?.includes('Running Back') || p.pos === 'RB' || p.rushYds > 0 || p.rushAtt > 0)
                        .sort((a, b) => {
                          // Sort by TDs first, then yards
                          const aTd = (a.rushTd || 0)
                          const bTd = (b.rushTd || 0)
                          if (bTd !== aTd) return bTd - aTd
                          return (b.rushYds || 0) - (a.rushYds || 0)
                        })
                        .map((player, idx) => {
                        const avg = player.rushAtt > 0 ? (player.rushYds / player.rushAtt).toFixed(1) : '0.0'
                        return (
                        <tr 
                          key={idx} 
                          className={`border-b border-gray-100 hover:bg-pink-50/40 transition cursor-pointer ${idx % 2 === 1 ? 'bg-gray-50/50' : ''}`}
                          onClick={() => setSelectedPlayer({ ...player, allProps: playerProps || [] })}
                        >
                          <td className={`py-2 px-2 sticky left-0 z-10 ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                            <span className="text-[11px] text-gray-700 truncate block font-medium">{player.name}</span>
                          </td>
                          <td className="text-right py-2 px-2 text-[11px] text-gray-700">{player.rushAtt || 0}</td>
                          <td className="text-right py-2 px-2 text-[11px] font-bold text-pink-600">{player.rushYds || 0}</td>
                          <td className="text-right py-2 px-2 text-[11px] text-gray-500">{avg}</td>
                          <td className="text-right py-2 px-2 text-[11px] text-gray-700">{player.rushTd || 0}</td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </>
              )}

              {/* Receiving Stats - ESPN style: REC, YDS, AVG, TD */}
              {currentTeam.players?.filter(p => p.pos?.includes('Wide Receiver') || p.pos?.includes('Tight End') || p.pos === 'WR' || p.pos === 'TE' || p.rec > 0).length > 0 && (
                <>
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 mt-2">
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Receiving</span>
                  </div>
                  <table className="w-full text-xs" style={{ fontVariantNumeric: 'tabular-nums', minWidth: '320px' }}>
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left py-2 px-2 font-semibold text-gray-500 sticky left-0 bg-gray-50 z-10" style={{ minWidth: '100px' }}>Player</th>
                        <th className="text-right py-2 px-2 font-semibold text-gray-400">REC</th>
                        <th 
                          onClick={() => handleSort('recYds')}
                          className={`text-right py-2 px-2 font-semibold cursor-pointer transition select-none ${sortBy === 'recYds' ? 'text-pink-600' : 'text-gray-400 hover:text-gray-600'}`}
                        >YDS</th>
                        <th className="text-right py-2 px-2 font-semibold text-gray-400">AVG</th>
                        <th 
                          onClick={() => handleSort('recTd')}
                          className={`text-right py-2 px-2 font-semibold cursor-pointer transition select-none ${sortBy === 'recTd' ? 'text-pink-600' : 'text-gray-400 hover:text-gray-600'}`}
                        >TD</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentTeam.players?.filter(p => p.pos?.includes('Wide Receiver') || p.pos?.includes('Tight End') || p.pos === 'WR' || p.pos === 'TE' || p.rec > 0)
                        .sort((a, b) => {
                          // Sort by TDs first, then yards
                          const aTd = (a.recTd || 0)
                          const bTd = (b.recTd || 0)
                          if (bTd !== aTd) return bTd - aTd
                          return (b.recYds || 0) - (a.recYds || 0)
                        })
                        .map((player, idx) => {
                        const avg = player.rec > 0 ? (player.recYds / player.rec).toFixed(1) : '0.0'
                        return (
                        <tr 
                          key={idx} 
                          className={`border-b border-gray-100 hover:bg-pink-50/40 transition cursor-pointer ${idx % 2 === 1 ? 'bg-gray-50/50' : ''}`}
                          onClick={() => setSelectedPlayer({ ...player, allProps: playerProps || [] })}
                        >
                          <td className={`py-2 px-2 sticky left-0 z-10 ${idx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}>
                            <span className="text-[11px] text-gray-700 truncate block font-medium">{player.name}</span>
                          </td>
                          <td className="text-right py-2 px-2 text-[11px] text-gray-700">{player.rec || 0}</td>
                          <td className="text-right py-2 px-2 text-[11px] font-bold text-pink-600">{player.recYds || 0}</td>
                          <td className="text-right py-2 px-2 text-[11px] text-gray-500">{avg}</td>
                          <td className="text-right py-2 px-2 text-[11px] text-gray-700">{player.recTd || 0}</td>
                        </tr>
                      )})}
                    </tbody>
                  </table>
                </>
              )}
            </div>
            ) : (
              /* NBA/Soccer Table - Clean Pro Layout */
              <>
                <div className="overflow-x-auto overscroll-x-contain no-scrollbar" style={{ WebkitOverflowScrolling: 'touch' }}>
                  <table className="w-full text-xs" style={{ minWidth: '400px', fontVariantNumeric: 'tabular-nums' }}>
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left py-3 px-3 font-semibold text-gray-500 sticky left-0 bg-gray-50 z-10" style={{ minWidth: '110px', maxWidth: '130px' }}>Player</th>
                        {(gameLeague?.toLowerCase() !== 'nfl') && (
                          <>
                            <th 
                              onClick={() => handleSort('pts')}
                              className={`text-right py-3 px-2 font-semibold w-10 cursor-pointer transition select-none ${sortBy === 'pts' ? 'text-pink-600' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                              PTS
                            </th>
                            <th className="text-right py-3 px-1.5 font-semibold text-gray-400 w-14">FG</th>
                            <th className="text-right py-3 px-2 font-semibold text-gray-400 w-10">3P</th>
                            <th 
                              onClick={() => handleSort('reb')}
                              className={`text-right py-3 px-2 font-semibold w-10 cursor-pointer transition select-none ${sortBy === 'reb' ? 'text-pink-600' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                              REB
                            </th>
                            <th 
                              onClick={() => handleSort('ast')}
                              className={`text-right py-3 px-2 font-semibold w-10 cursor-pointer transition select-none ${sortBy === 'ast' ? 'text-pink-600' : 'text-gray-400 hover:text-gray-600'}`}
                            >
                              AST
                            </th>
                            <th className="text-right py-3 px-2 font-semibold text-gray-400 w-8">S</th>
                            <th className="text-right py-3 px-2 font-semibold text-gray-400 w-8">B</th>
                            <th className="text-right py-3 px-2 font-semibold text-gray-300 w-8">M</th>
                          </>
                        )}
                        {league === 'Soccer' && (
                          <>
                            <th className="text-right py-3 px-2 font-semibold text-gray-500 w-10">G</th>
                            <th className="text-right py-3 px-2 font-semibold text-gray-500 w-10">A</th>
                            <th className="text-right py-3 px-2 font-semibold text-gray-500 w-10">SH</th>
                            <th className="text-right py-3 px-2 font-semibold text-gray-500 w-10">YC</th>
                            <th className="text-right py-3 px-2 font-semibold text-gray-500 w-10">RC</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {/* STARTERS (first 5) */}
                      {starters.map((player, idx) => (
                        <tr 
                          key={`starter-${idx}`} 
                          className={`border-b border-gray-100 hover:bg-pink-50/40 transition cursor-pointer ${idx % 2 === 1 ? 'bg-gray-50/50' : ''}`}
                          onClick={() => setSelectedPlayer({ ...player, allProps: playerProps || [] })}
                        >
                          <td className={`py-3 px-3 sticky left-0 z-10 bg-white ${idx % 2 === 1 ? 'bg-gray-50' : ''}`} style={{ backgroundColor: idx % 2 === 1 ? '#fafafa' : 'white' }}>
                            <span className="text-[12px] text-gray-700 truncate block" style={{ maxWidth: '120px' }}>
                              {player.name}
                            </span>
                          </td>
                          {(gameLeague?.toLowerCase() !== 'nfl') && (
                            <>
                              <td className="text-right py-3 px-2 font-bold text-pink-600">{player.pts}</td>
                              <td className="text-right py-3 px-1.5 text-gray-500 whitespace-nowrap">{player.fgm || 0}-{player.fga || 0}</td>
                              <td className="text-right py-3 px-2 text-gray-500">{player.fg3m || player.three || 0}</td>
                              <td className="text-right py-3 px-2 text-gray-700">{player.reb}</td>
                              <td className="text-right py-3 px-2 text-gray-700">{player.ast}</td>
                              <td className="text-right py-3 px-2 text-gray-400">{player.stl || 0}</td>
                              <td className="text-right py-3 px-2 text-gray-400">{player.blk || 0}</td>
                              <td className="text-right py-3 px-2 text-gray-300">{player.min || '-'}</td>
                            </>
                          )}
                          {league === 'Soccer' && (
                            <>
                              <td className="text-right py-3 px-2 font-bold text-pink-600">{player.goals || 0}</td>
                              <td className="text-right py-3 px-2 text-gray-700">{player.assists || 0}</td>
                              <td className="text-right py-3 px-2 text-gray-600">{player.shots || 0}</td>
                              <td className="text-right py-3 px-2 text-yellow-500">{player.yellowCards || 0}</td>
                              <td className="text-right py-3 px-2 text-red-500">{player.redCards || 0}</td>
                            </>
                          )}
                        </tr>
                      ))}
                      
                      
                      {/* BENCH - with divider */}
                      {bench.length > 0 && showBench && (
                        <>
                          <tr className="bg-gray-100">
                            <td colSpan={10} className="py-1.5 px-3 text-[10px] font-semibold text-gray-500 uppercase tracking-wide sticky left-0 bg-gray-100">
                              Bench
                            </td>
                          </tr>
                          {bench.map((player, idx) => (
                            <tr 
                              key={`bench-${idx}`} 
                              className={`border-b border-gray-100 hover:bg-pink-50/40 transition cursor-pointer ${idx % 2 === 1 ? 'bg-gray-50/30' : ''}`}
                              onClick={() => setSelectedPlayer({ ...player, allProps: playerProps || [] })}
                            >
                              <td className="py-3 px-3 sticky left-0 z-10 bg-white" style={{ backgroundColor: idx % 2 === 1 ? '#f5f5f5' : 'white' }}>
                                <span className="text-[12px] text-gray-500 truncate block" style={{ maxWidth: '120px' }}>
                                  {player.name}
                                </span>
                              </td>
                              {(gameLeague?.toLowerCase() !== 'nfl') && (
                                <>
                                  <td className="text-right py-3 px-2 font-semibold text-pink-500">{player.pts}</td>
                                  <td className="text-right py-3 px-2 text-gray-400">{player.fgm || 0}-{player.fga || 0}</td>
                                  <td className="text-right py-3 px-2 text-gray-400">{player.fg3m || player.three || 0}</td>
                                  <td className="text-right py-3 px-2 text-gray-600">{player.reb}</td>
                                  <td className="text-right py-3 px-2 text-gray-600">{player.ast}</td>
                                  <td className="text-right py-3 px-2 text-gray-400">{player.stl || 0}</td>
                                  <td className="text-right py-3 px-2 text-gray-400">{player.blk || 0}</td>
                                  <td className="text-right py-3 px-2 text-gray-300">{player.min || '-'}</td>
                                </>
                              )}
                              {league === 'Soccer' && (
                                <>
                                  <td className="text-right py-3 px-2 font-bold text-pink-600">{player.goals || 0}</td>
                                  <td className="text-right py-3 px-2 text-gray-600">{player.assists || 0}</td>
                                  <td className="text-right py-3 px-2 text-gray-600">{player.shots || 0}</td>
                                  <td className="text-right py-3 px-2 text-yellow-500">{player.yellowCards || 0}</td>
                                  <td className="text-right py-3 px-2 text-red-500">{player.redCards || 0}</td>
                                </>
                              )}
                            </tr>
                          ))}
                        
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Show bench button */}
                {bench.length > 0 && (
                  <button
                    onClick={() => setShowBench(!showBench)}
                    className={`w-full py-2.5 text-xs font-semibold border-t transition flex items-center justify-center gap-1 ${
                      showBench 
                        ? 'text-gray-500 bg-gray-50 hover:bg-gray-100' 
                        : 'text-pink-600 bg-pink-50/50 hover:bg-pink-50'
                    }`}
                  >
                    {showBench ? 'Hide bench ▴' : `Show bench (${bench.length}) ▾`}
                  </button>
                )}
              </>
            )}
          </div>
          
          {/* Game Injuries - BELOW player stats */}
          <GameInjuries game={game} league={league} />
        </div>
      )}
    </div>
  )
}
